import React, { useState, useEffect } from "react";
import {
  FiActivity,
  FiCoffee,
  FiTrendingUp,
  FiTarget,
  FiAward,
  FiCalendar,
  FiZap,
  FiCheckCircle,
} from "react-icons/fi";
import MonthlySalesChart from "../../../tailadmin/components/ecommerce/MonthlySalesChart";
import PieChart from "../../../tailadmin/components/ecommerce/PieChart";
import reports from "../../../services/reports";
import Skeleton from "../../ui/Skeleton";

interface StudentActivityVsNutritionDashboardProps {
  studentId: string | number;
  professionalId?: string | number;
}

export default function StudentActivityVsNutritionDashboard({
  studentId,
  professionalId,
}: StudentActivityVsNutritionDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<number | null>(30);
  const [stats, setStats] = useState({
    days_analyzed: 30,
    summary: {
      total_workouts: 0,
      total_meals: 0,
      total_calories: 0,
      workout_adherence: 0,
      nutrition_adherence: 0,
      consistency_rate: 0,
      balance_score: 0,
    },
    tracking_overlap: {
      days_with_both: 0,
      days_with_only_workout: 0,
      days_with_only_nutrition: 0,
      days_with_neither: 0,
      overlap_percentage: 0,
    },
    daily_comparison: [] as Array<{
      date: string;
      workouts: number;
      meals: number;
      calories: number;
      has_activity: boolean;
      has_nutrition: boolean;
      both_tracked: boolean;
    }>,
    weekday_comparison: [] as Array<{
      day: string;
      avg_workouts: number;
      avg_meals: number;
      avg_calories: number;
      total_workouts: number;
      total_meals: number;
    }>,
    correlations: {
      avg_workouts_when_both: 0,
      avg_calories_when_both: 0,
      avg_calories_with_workout: 0,
      avg_calories_without_workout: 0,
      calorie_difference: 0,
    },
    nutrition_summary: undefined as
      | {
          total_proteina_g: number;
          total_carboidrato_g: number;
          total_lipideos_g: number;
          total_fibra_g: number;
          avg_proteina_g: number;
          avg_carboidrato_g: number;
          avg_lipideos_g: number;
          avg_fibra_g: number;
          distribuicao_macros: {
            proteina_pct: number;
            carboidrato_pct: number;
            lipideos_pct: number;
          };
          meals_with_taco_data: number;
          meals_without_taco_data: number;
        }
      | undefined,
  });

  useEffect(() => {
    async function fetchData() {
      if (!studentId) return;

      setLoading(true);
      try {
        const data = await reports.getActivityVsNutritionComparison(
          studentId,
          professionalId,
          dateFilter || undefined,
        );
        setStats({
          ...data,
          nutrition_summary: data.nutrition_summary || undefined,
        });
      } catch (error) {
        console.error(
          "Erro ao buscar comparação atividade vs nutrição:",
          error,
        );
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [studentId, professionalId, dateFilter]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  // Preparar dados para gráficos
  const dailyCategories = stats.daily_comparison.map((d) => formatDate(d.date));
  const workoutsData = stats.daily_comparison.map((d) => d.workouts);
  const mealsData = stats.daily_comparison.map((d) => d.meals);

  // Dados para gráfico de pizza de sobreposição
  const overlapLabels = [
    "Ambos Registrados",
    "Só Treino",
    "Só Alimentação",
    "Nenhum",
  ];
  const overlapData = [
    stats.tracking_overlap.days_with_both,
    stats.tracking_overlap.days_with_only_workout,
    stats.tracking_overlap.days_with_only_nutrition,
    stats.tracking_overlap.days_with_neither,
  ];
  const overlapColors = ["#10b981", "#85368c", "#f59e0b", "#6b7280"];

  const filterOptions = [
    { label: "7 dias", value: 7 },
    { label: "15 dias", value: 15 },
    { label: "30 dias", value: 30 },
    { label: "60 dias", value: 60 },
    { label: "90 dias", value: 90 },
  ];

  // Determinar cor do score de balanço
  const getBalanceScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600 dark:text-green-400";
    if (score >= 60) return "text-blue-600 dark:text-blue-400";
    if (score >= 40) return "text-orange-600 dark:text-orange-400";
    return "text-red-600 dark:text-red-400";
  };

  const getBalanceScoreBgColor = (score: number) => {
    if (score >= 80) return "bg-green-100 dark:bg-green-900/30";
    if (score >= 60) return "bg-blue-100 dark:bg-blue-900/30";
    if (score >= 40) return "bg-orange-100 dark:bg-orange-900/30";
    return "bg-red-100 dark:bg-red-900/30";
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]"
            >
              <Skeleton className="h-8 w-8 sm:h-10 sm:w-10 rounded-full" />
              <div className="mt-3 sm:mt-4">
                <Skeleton className="mb-2 h-5 sm:h-6 w-20 sm:w-24" />
                <Skeleton className="h-3 sm:h-4 w-28 sm:w-32" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header com filtro */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
            Comparativo: Atividade vs Alimentação
          </h4>
          <p className="mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            Análise integrada da relação entre treinos e hábitos alimentares
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {filterOptions.map((option) => (
            <button
              key={option.label}
              onClick={() => setDateFilter(option.value)}
              className={`rounded-lg sm:rounded-md px-3 py-2 sm:py-1.5 text-xs sm:text-sm font-medium transition active:scale-95 touch-manipulation ${
                dateFilter === option.value
                  ? "bg-purple-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-meta-4 dark:text-gray-300 dark:hover:bg-opacity-80"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Métricas Principais */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Score de Balanço */}
        <div className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
          <div
            className={`flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg sm:rounded-xl ${getBalanceScoreBgColor(stats.summary.balance_score)}`}
          >
            <FiAward
              className={`h-5 w-5 sm:h-6 sm:w-6 ${getBalanceScoreColor(stats.summary.balance_score)}`}
            />
          </div>
          <div className="mt-4 sm:mt-5">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Score de Balanço
            </p>
            <h4
              className={`mt-1.5 sm:mt-2 text-xl sm:text-2xl font-bold ${getBalanceScoreColor(stats.summary.balance_score)}`}
            >
              {stats.summary.balance_score}
            </h4>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {stats.summary.balance_score >= 80
                ? "Excelente equilíbrio"
                : stats.summary.balance_score >= 60
                  ? "Bom equilíbrio"
                  : stats.summary.balance_score >= 40
                    ? "Equilíbrio moderado"
                    : "Precisa melhorar"}
            </p>
          </div>
        </div>

        {/* Taxa de Consistência */}
        <div className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg sm:rounded-xl bg-green-100 dark:bg-green-900/30">
            <FiCheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
          </div>
          <div className="mt-4 sm:mt-5">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Taxa de Consistência
            </p>
            <h4 className="mt-1.5 sm:mt-2 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {stats.summary.consistency_rate}%
            </h4>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {stats.tracking_overlap.days_with_both} dias com ambos
            </p>
          </div>
        </div>

        {/* Aderência a Treinos */}
        <div className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg sm:rounded-xl bg-purple-100 dark:bg-purple-900/30">
            <FiActivity className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="mt-4 sm:mt-5">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Aderência a Treinos
            </p>
            <h4 className="mt-1.5 sm:mt-2 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {stats.summary.workout_adherence}%
            </h4>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {stats.summary.total_workouts} treinos registrados
            </p>
          </div>
        </div>

        {/* Aderência à Alimentação */}
        <div className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg sm:rounded-xl bg-orange-100 dark:bg-orange-900/30">
            <FiCoffee className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <div className="mt-4 sm:mt-5">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Aderência à Alimentação
            </p>
            <h4 className="mt-1.5 sm:mt-2 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {stats.summary.nutrition_adherence}%
            </h4>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {stats.summary.total_meals} refeições registradas
            </p>
          </div>
        </div>
      </div>

      {/* Insights de Correlação */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-2">
        {/* Calorias e Treinos */}
        <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white p-4 sm:p-6 shadow-sm dark:border-strokedark dark:bg-boxdark">
          <h5 className="mb-3 sm:mb-4 text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FiZap className="text-purple-600 dark:text-purple-400" />
            Correlação: Calorias e Atividade
          </h5>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Calorias em dias COM treino
              </span>
              <span className="text-base sm:text-lg font-bold text-green-600 dark:text-green-400">
                {stats.correlations.avg_calories_with_workout.toLocaleString(
                  "pt-BR",
                )}{" "}
                kcal
              </span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Calorias em dias SEM treino
              </span>
              <span className="text-base sm:text-lg font-bold text-orange-600 dark:text-orange-400">
                {stats.correlations.avg_calories_without_workout.toLocaleString(
                  "pt-BR",
                )}{" "}
                kcal
              </span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
              <span className="text-xs sm:text-sm font-semibold text-purple-700 dark:text-purple-300">
                Diferença
              </span>
              <span className="text-base sm:text-lg font-bold text-purple-600 dark:text-purple-400">
                {Math.abs(stats.correlations.calorie_difference) > 0 ? (
                  <>
                    {stats.correlations.calorie_difference > 0 ? "+" : ""}
                    {stats.correlations.calorie_difference.toLocaleString(
                      "pt-BR",
                    )}{" "}
                    kcal
                  </>
                ) : (
                  "Sem diferença"
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Distribuição de Registro */}
        <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white p-4 sm:p-6 shadow-sm dark:border-strokedark dark:bg-boxdark">
          <h5 className="mb-3 sm:mb-4 text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FiTarget className="text-green-600 dark:text-green-400" />
            Sobreposição de Registros
          </h5>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div className="text-center p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
              <div className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400">
                {stats.tracking_overlap.days_with_both}
              </div>
              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                Ambos
              </div>
            </div>
            <div className="text-center p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20">
              <div className="text-2xl sm:text-3xl font-bold text-purple-600 dark:text-purple-400">
                {stats.tracking_overlap.days_with_only_workout}
              </div>
              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                Só Treino
              </div>
            </div>
            <div className="text-center p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20">
              <div className="text-2xl sm:text-3xl font-bold text-orange-600 dark:text-orange-400">
                {stats.tracking_overlap.days_with_only_nutrition}
              </div>
              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                Só Alimentação
              </div>
            </div>
            <div className="text-center p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
              <div className="text-2xl sm:text-3xl font-bold text-gray-600 dark:text-gray-400">
                {stats.tracking_overlap.days_with_neither}
              </div>
              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                Nenhum
              </div>
            </div>
          </div>
          <div className="mt-3 sm:mt-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-center">
            <span className="text-xs sm:text-sm text-blue-700 dark:text-blue-300 font-semibold">
              Taxa de Sobreposição: {stats.tracking_overlap.overlap_percentage}%
            </span>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Comparação Diária - Treinos */}
        {stats.daily_comparison.length > 0 && (
          <>
            <div className="col-span-1 h-[300px] sm:h-[350px] lg:h-[400px]">
              <MonthlySalesChart
                title="Frequência de Treinos"
                data={workoutsData}
                categories={dailyCategories}
                tooltipText="Número de treinos realizados por dia no período selecionado"
              />
            </div>
            <div className="col-span-1 h-[300px] sm:h-[350px] lg:h-[400px]">
              <MonthlySalesChart
                title="Frequência de Refeições"
                data={mealsData}
                categories={dailyCategories}
                tooltipText="Número de refeições registradas por dia no período selecionado"
              />
            </div>
          </>
        )}

        {/* Gráfico de Pizza - Distribuição de Registros */}
        {overlapData.some((v) => v > 0) && (
          <PieChart
            title="Distribuição de Registros"
            data={overlapData}
            labels={overlapLabels}
            colors={overlapColors}
            tooltipText="Este gráfico mostra a distribuição de dias com diferentes padrões de registro: dias com treino e alimentação, apenas treino, apenas alimentação, ou nenhum registro."
          />
        )}

        {/* Comparação por Dia da Semana */}
        {stats.weekday_comparison.length > 0 && (
          <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white p-4 sm:p-6 shadow-sm dark:border-strokedark dark:bg-boxdark">
            <h5 className="mb-3 sm:mb-4 text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <FiCalendar className="text-blue-600 dark:text-blue-400" />
              Atividade por Dia da Semana
            </h5>
            <div className="space-y-2 sm:space-y-3">
              {stats.weekday_comparison.map((day, index) => (
                <div
                  key={day.day}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-purple-100 text-xs sm:text-sm font-bold text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
                      {index + 1}
                    </div>
                    <span className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">
                      {day.day}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="text-right">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Exercícios
                      </div>
                      <div className="text-sm sm:text-base font-bold text-purple-600 dark:text-purple-400">
                        {day.total_workouts}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Refeições
                      </div>
                      <div className="text-sm sm:text-base font-bold text-orange-600 dark:text-orange-400">
                        {day.total_meals}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Estado vazio */}
      {stats.summary.total_workouts === 0 &&
        stats.summary.total_meals === 0 &&
        !loading && (
          <div className="flex flex-col items-center justify-center rounded-xl sm:rounded-2xl border-2 border-dashed border-gray-300 bg-white py-12 sm:py-16 px-4 dark:border-gray-700 dark:bg-boxdark">
            <div className="rounded-full bg-gradient-to-br from-purple-100 to-purple-100 p-5 sm:p-6 dark:from-purple-900/30 dark:to-purple-900/30">
              <FiTrendingUp
                size={40}
                className="sm:w-12 sm:h-12 text-gray-400 dark:text-gray-600"
              />
            </div>
            <p className="mt-4 sm:mt-5 text-base sm:text-lg font-semibold text-gray-700 dark:text-gray-300 text-center">
              Nenhum dado registrado
            </p>
            <p className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-500 text-center max-w-sm">
              Os dados de comparação aparecerão aqui quando o aluno começar a
              registrar treinos e alimentação
            </p>
          </div>
        )}
    </div>
  );
}
